import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  User,
} from "firebase/auth";
import { DINECENTRAL_AUTH } from "../../Firebase.dinecentral";
import { SignInWithEmailDineCentral } from "../../SignIn.dinecentral";
import {
  useDineCentralAuth,
  savePendingMarketingPreference,
} from "../../hooks/useDineCentralAuth";

type View =
  | "signin"
  | "signup"
  | "forgotPassword"
  | "passwordResetSent"
  | "verifyEmail"
  | "verificationSent"
  | "redirecting";

// autoFocus only where it helps (keyboard+mouse): on iOS a programmatic
// focus during mount makes Safari zoom hard into the half-laid-out field
// (and never opens the keyboard anyway — it needs a real tap).
const AUTO_FOCUS_OK =
  typeof window === "undefined" || !window.matchMedia?.("(pointer: coarse)")?.matches;

const DineCentralLogin: React.FC = () => {
  const navigate = useNavigate();
  const {
    processPendingMarketingPreference,
    verificationCooldown,
    setVerificationEmailSent,
    startCooldownTimer,
  } = useDineCentralAuth();

  const redirectUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("redirect") || "/";
  }, []);

  const [view, setView] = useState<View>("signin");
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const goTo = useCallback((v: View) => {
    setError("");
    setView(v);
  }, []);

  // The login page is a chrome-less overlay — without this there is no way
  // out except signing in. Back where there's history, home otherwise.
  const goBack = useCallback(() => {
    const idx = (window.history.state && typeof window.history.state.idx === "number") ? window.history.state.idx : 0;
    if (idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(DINECENTRAL_AUTH, async (user) => {
      if (user) {
        await user.reload();
        const refreshed = DINECENTRAL_AUTH.currentUser;
        setCurrentUser(refreshed);
        if (refreshed && !refreshed.emailVerified) {
          const savedView = localStorage.getItem("dc_loginView");
          if (savedView === "verificationSent") {
            goTo("verificationSent");
          } else {
            goTo("verifyEmail");
          }
        } else if (refreshed?.emailVerified) {
          localStorage.removeItem("dc_loginView");
          goTo("redirecting");
          // replace, not push: an already-signed-in visit to /login used to
          // PUSH the redirect, so the browser back button just bounced
          // forward again (and stacked history entries doing it).
          navigate(redirectUrl, { replace: true });
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, redirectUrl]);

  // ── Sign in ──────────────────────────────────────────────────
  const handleSignIn = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      setIsLoading(true);
      const result = await SignInWithEmailDineCentral(email, password);
      if (result === "SUCCESS") {
        navigate(redirectUrl, { replace: true });
      } else if (result === "NOT_VERIFIED") {
        goTo("verifyEmail");
      } else {
        setError(result.replace("ERROR: ", ""));
      }
      setIsLoading(false);
    },
    [email, password, navigate, redirectUrl, goTo]
  );

  // ── Sign up ──────────────────────────────────────────────────
  const handleSignUp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      if (!name.trim()) return setError("Name is required.");
      if (password !== passwordConfirm) return setError("Passwords do not match.");
      if (!agreeToTerms) return setError("You must agree to the Terms and Privacy Policy.");

      setIsLoading(true);
      try {
        const result = await createUserWithEmailAndPassword(DINECENTRAL_AUTH, email, password);
        await updateProfile(result.user, { displayName: name });

        if (marketingConsent) {
          savePendingMarketingPreference(email, true);
        }

        await sendEmailVerification(result.user);
        setCurrentUser(result.user);

        startCooldownTimer(60);
        setVerificationEmailSent(true);
        localStorage.setItem("dc_verificationEmailSent", "true");
        localStorage.setItem("dc_loginView", "verificationSent");

        goTo("verificationSent");
      } catch (err: any) {
        setError(formatFirebaseError(err.message));
      }
      setIsLoading(false);
    },
    [name, email, password, passwordConfirm, agreeToTerms, marketingConsent, goTo, startCooldownTimer, setVerificationEmailSent]
  );

  // ── Forgot password ──────────────────────────────────────────
  const handleForgotPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      setIsLoading(true);
      try {
        await sendPasswordResetEmail(DINECENTRAL_AUTH, email);
        goTo("passwordResetSent");
      } catch (err: any) {
        setError(formatFirebaseError(err.message));
      }
      setIsLoading(false);
    },
    [email, goTo]
  );

  // ── Resend verification ──────────────────────────────────────
  const handleResendVerification = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!currentUser || verificationCooldown > 0) return;
      setIsLoading(true);
      try {
        await sendEmailVerification(currentUser);
        startCooldownTimer(60);
        setVerificationEmailSent(true);
        localStorage.setItem("dc_verificationEmailSent", "true");
        localStorage.setItem("dc_loginView", "verificationSent");
        goTo("verificationSent");
      } catch (err: any) {
        setError(formatFirebaseError(err.message));
      }
      setIsLoading(false);
    },
    [currentUser, verificationCooldown, goTo, startCooldownTimer, setVerificationEmailSent]
  );

  // ── "I've verified my email" ─────────────────────────────────
  const handleCheckVerification = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);
    try {
      await currentUser.reload();
      const refreshed = DINECENTRAL_AUTH.currentUser;
      if (refreshed?.emailVerified) {
        if (refreshed.email) {
          await processPendingMarketingPreference(refreshed.email);
        }
        localStorage.removeItem("dc_loginView");
        navigate(redirectUrl, { replace: true });
      } else {
        setError("Your email hasn't been verified yet. Please check your inbox.");
      }
    } catch (err: any) {
      setError(formatFirebaseError(err.message));
    }
    setIsLoading(false);
  }, [currentUser, navigate, redirectUrl, processPendingMarketingPreference]);

  // ── Sign out ─────────────────────────────────────────────────
  const handleSignOut = useCallback(() => {
    signOut(DINECENTRAL_AUTH);
    localStorage.removeItem("dc_loginView");
    localStorage.removeItem("dc_verificationEmailSent");
    localStorage.removeItem("dc_verificationCooldownExpires");
    setCurrentUser(null);
    goTo("signin");
  }, [goTo]);

  if (view === "redirecting") {
    return (
      <div style={styles.page}>
        <div style={styles.redirectingText}>Signing you in…</div>
      </div>
    );
  }

  const showToggle = view === "signin" || view === "signup";

  return (
    <>
      <style>{css}</style>
      <div style={styles.page}>
        <div style={styles.backdrop}>
          <div style={styles.blobA} />
          <div style={styles.blobB} />
        </div>

        <button type="button" style={styles.backBtn} onClick={goBack} aria-label="Go back">
          <span style={{ fontSize: "16px", lineHeight: 1 }}>←</span> Back
        </button>

        <div style={styles.card}>
          {/* Logo */}
          <div style={styles.logoRow}>
            <span style={{ fontWeight: 800, color: "white", fontSize: "28px", letterSpacing: "-0.5px" }}>
              Dine<span style={{ fontWeight: 800, color: "royalblue", fontSize: "28px" }}>Platform</span>
            </span>
          </div>

          {/* Toggle — only on signin / signup */}
          {showToggle && (
            <div style={styles.toggle}>
              <button
                type="button"
                className={`dc-toggle-btn${view === "signin" ? " dc-toggle-active" : ""}`}
                onClick={() => goTo("signin")}
              >
                Sign in
              </button>
              <button
                type="button"
                className={`dc-toggle-btn${view === "signup" ? " dc-toggle-active" : ""}`}
                onClick={() => goTo("signup")}
              >
                Sign up
              </button>
            </div>
          )}

          {/* ── Sign in ── */}
          {view === "signin" && (
            <>
              <form onSubmit={handleSignIn} style={styles.form}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <input className="dc-input" type="email" placeholder="you@example.com"
                    value={email} required autoFocus={AUTO_FOCUS_OK} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Password</label>
                  <input className="dc-input" type="password" placeholder="••••••••"
                    value={password} required onChange={(e) => setPassword(e.target.value)} />
                  <span className="dc-link" style={{ marginTop: "4px", fontSize: "12px" }}
                    onClick={() => goTo("forgotPassword")}>
                    Forgot password?
                  </span>
                </div>
                <button type="submit" className="dc-btn dc-btn-primary" disabled={isLoading}>
                  {isLoading ? "Signing in…" : "Sign in"}
                </button>
              </form>
              {error && <p style={styles.errorText}>{error}</p>}
            </>
          )}

          {/* ── Sign up ── */}
          {view === "signup" && (
            <>
              <form onSubmit={handleSignUp} style={styles.form}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Full name</label>
                  <input className="dc-input" type="text" placeholder="Jane Smith"
                    value={name} required autoFocus={AUTO_FOCUS_OK} onChange={(e) => setName(e.target.value)} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <input className="dc-input" type="email" placeholder="you@example.com"
                    value={email} required onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Password</label>
                  <input className="dc-input" type="password" placeholder="Min. 8 characters"
                    value={password} required minLength={8} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Confirm password</label>
                  <input className="dc-input" type="password" placeholder="••••••••"
                    value={passwordConfirm} required minLength={8} onChange={(e) => setPasswordConfirm(e.target.value)} />
                </div>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    style={{ marginRight: "8px", accentColor: "royalblue", flexShrink: 0 }} />
                  <span style={{ fontSize: "13px", color: "#b0b0b0" }}>
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacyPolicy" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>Privacy Policy</a>
                  </span>
                </label>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    style={{ marginRight: "8px", accentColor: "royalblue", flexShrink: 0 }} />
                  <span style={{ fontSize: "13px", color: "#b0b0b0" }}>
                    I'd like to receive updates about special offers and new restaurants
                  </span>
                </label>
                <button type="submit" className="dc-btn dc-btn-primary" disabled={isLoading}>
                  {isLoading ? "Creating account…" : "Create account"}
                </button>
              </form>
              {error && <p style={styles.errorText}>{error}</p>}
            </>
          )}

          {/* ── Forgot password ── */}
          {view === "forgotPassword" && (
            <>
              <h1 style={styles.heading}>Reset password</h1>
              <p style={styles.subheading}>We'll send a reset link to your email</p>
              <form onSubmit={handleForgotPassword} style={styles.form}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <input className="dc-input" type="email" placeholder="you@example.com"
                    value={email} required autoFocus={AUTO_FOCUS_OK} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button type="submit" className="dc-btn dc-btn-primary" disabled={isLoading}>
                  {isLoading ? "Sending…" : "Send reset link"}
                </button>
                <button type="button" className="dc-btn dc-btn-ghost" onClick={() => goTo("signin")}>
                  ← Back to sign in
                </button>
              </form>
              {error && <p style={styles.errorText}>{error}</p>}
            </>
          )}

          {/* ── Password reset sent ── */}
          {view === "passwordResetSent" && (
            <div style={styles.messageContainer}>
              <h1 style={styles.heading}>Check your email</h1>
              <p style={styles.subheading}>
                A password reset link has been sent to{" "}
                <strong style={{ color: "#e8e8e8" }}>{email}</strong>.
              </p>
              <button className="dc-btn dc-btn-primary" onClick={() => goTo("signin")}>
                Back to sign in
              </button>
            </div>
          )}

          {/* ── Verify email ── */}
          {view === "verifyEmail" && (
            <div style={styles.messageContainer}>
              <h1 style={styles.heading}>Verify your email</h1>
              <p style={styles.subheading}>
                Please verify <strong style={{ color: "#e8e8e8" }}>{currentUser?.email}</strong> before
                signing in.
              </p>
              <div style={{ ...styles.form, width: "100%" }}>
                <button className="dc-btn dc-btn-primary" onClick={handleCheckVerification} disabled={isLoading}>
                  {isLoading ? "Checking…" : "I've verified my email"}
                </button>
                <button
                  className="dc-btn dc-btn-ghost"
                  onClick={handleResendVerification}
                  disabled={verificationCooldown > 0 || isLoading}
                >
                  {verificationCooldown > 0 ? `Resend (${verificationCooldown}s)` : "Resend verification email"}
                </button>
                <button className="dc-btn dc-btn-ghost"
                  style={{ borderColor: "rgba(255,80,80,0.3)", color: "#f87171" }}
                  onClick={handleSignOut}>
                  Log out
                </button>
              </div>
              {error && <p style={styles.errorText}>{error}</p>}
            </div>
          )}

          {/* ── Verification sent ── */}
          {view === "verificationSent" && (
            <div style={styles.messageContainer}>
              <h1 style={styles.heading}>Check your inbox</h1>
              <p style={styles.subheading}>
                A verification link has been sent to <strong style={{ color: "#e8e8e8" }}>{currentUser?.email}</strong>.
                Click it to activate your account, then return here.
              </p>
              <div style={{ ...styles.form, width: "100%" }}>
                <button className="dc-btn dc-btn-primary" onClick={handleCheckVerification} disabled={isLoading}>
                  {isLoading ? "Checking…" : "I've verified my email"}
                </button>
                <button
                  className="dc-btn dc-btn-ghost"
                  onClick={handleResendVerification}
                  disabled={verificationCooldown > 0 || isLoading}
                >
                  {verificationCooldown > 0 ? `Resend (${verificationCooldown}s)` : "Resend verification email"}
                </button>
                <button className="dc-btn dc-btn-ghost"
                  style={{ borderColor: "rgba(255,80,80,0.3)", color: "#f87171" }}
                  onClick={handleSignOut}>
                  Log out
                </button>
              </div>
              {error && <p style={styles.errorText}>{error}</p>}
            </div>
          )}

          {/* Store login link — signin / signup only */}
          {showToggle && (
            <div style={styles.storeLoginRow}>
              <a href="/store-login" style={styles.storeLoginLink}>Store login →</a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* ─── Helpers ────────────────────────────────────────────────── */
const formatFirebaseError = (message: string): string => {
  try {
    const code = message.split("/")[1].split(")")[0].replaceAll("-", " ");
    return code.charAt(0).toUpperCase() + code.slice(1);
  } catch {
    return message;
  }
};

/* ─── Styles ─────────────────────────────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
  // The PAGE is the scroller (not the card): a fixed overlay with
  // overflow hidden clipped tall forms (signup, keyboard open) with no way
  // to scroll them into view. The card centers via margin:auto — unlike
  // flex centering, that never clips the top edge when content overflows.
  page: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#0a0a0a",
    display: "flex",
    padding: "24px",
    overflowY: "auto",
    overflowX: "hidden", /* vertical scroll only — the decorative blobs hang past the edges */
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    boxSizing: "border-box",
  },
  backBtn: {
    position: "fixed", /* the page scrolls now — keep Back pinned in view */
    top: "22px",
    left: "22px",
    zIndex: 2,
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.16)",
    color: "rgba(255,255,255,0.85)",
    borderRadius: "999px",
    padding: "9px 16px",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  // Blobs live in a FIXED, clipped backdrop: as direct children of the
  // scrolling page their negative offsets inflated scrollWidth/scrollHeight
  // (sideways scrolling, dead space past the form).
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 0,
  },
  blobA: {
    position: "absolute",
    top: "-120px",
    right: "-80px",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(65,105,225,0.12) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  blobB: {
    position: "absolute",
    bottom: "-140px",
    left: "-100px",
    width: "520px",
    height: "520px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(65,105,225,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: "400px",
    /* border-box: without it, 100% width PLUS the side paddings overflowed
       the viewport by ~74px on phones (sideways scroll / clipped edges). */
    boxSizing: "border-box",
    margin: "auto", /* centers in the scrolling page without clipping */
    backgroundColor: "#141414",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "20px",
    padding: "36px 26px 30px",
    boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
    flexShrink: 0,
  },
  logoRow: {
    marginBottom: "28px",
    textAlign: "center" as const,
  },
  toggle: {
    display: "flex",
    backgroundColor: "#1a1a1a",
    borderRadius: "10px",
    padding: "4px",
    marginBottom: "28px",
    border: "1px solid rgba(255,255,255,0.07)",
  },
  heading: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#ffffff",
    margin: "0 0 6px 0",
    letterSpacing: "-0.5px",
  },
  subheading: {
    fontSize: "15px",
    color: "#b0b0b0",
    margin: "0 0 24px 0",
    lineHeight: "1.5",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "14px",
    color: "#d0d0d0",
    fontWeight: 500,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    cursor: "pointer",
  },
  termsLink: {
    color: "royalblue",
    textDecoration: "none",
  },
  errorText: {
    color: "#ff6b6b",
    fontSize: "14px",
    marginTop: "12px",
    textAlign: "center",
  },
  storeLoginRow: {
    marginTop: "24px",
    textAlign: "center" as const,
  },
  storeLoginLink: {
    fontSize: "13px",
    color: "#888",
    textDecoration: "none",
    transition: "color 0.2s",
  },
  messageContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    textAlign: "center" as const,
    gap: "12px",
  },
  iconCircle: {
    fontSize: "36px",
    marginBottom: "8px",
  },
  redirectingText: {
    color: "#b0b0b0",
    fontSize: "15px",
    fontFamily: "'DM Sans', sans-serif",
  },
};

/* ─── CSS ────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

  .dc-btn {
    width: 100%;
    padding: 13px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  }

  .dc-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .dc-btn-primary {
    background: #4169E1;
    color: #ffffff;
    box-shadow: 0 4px 20px rgba(65,105,225,0.3);
  }

  .dc-btn-primary:hover:not(:disabled) {
    background: #5578e8;
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(65,105,225,0.4);
  }

  .dc-btn-ghost {
    background: transparent;
    color: #999;
    border: 1px solid rgba(255,255,255,0.08);
    font-weight: 500;
  }

  .dc-btn-ghost:hover:not(:disabled) {
    color: #bbb;
    background: rgba(255,255,255,0.03);
  }

  .dc-toggle-btn {
    flex: 1;
    padding: 9px 12px;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: #888;
    font-size: 14px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .dc-toggle-btn:hover:not(.dc-toggle-active) {
    color: #bbb;
  }

  .dc-toggle-active {
    background: #4169E1;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(65,105,225,0.35);
  }

  .dc-input {
    background: #1a1a1a;
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 9px;
    padding: 12px 14px;
    font-size: 16px; /* below 16px iOS zooms into the focused field */
    font-family: 'DM Sans', sans-serif;
    color: #e8e8e8;
    width: 100%;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .dc-input::placeholder {
    color: #555;
  }

  .dc-input:focus {
    border-color: rgba(65,105,225,0.5);
    box-shadow: 0 0 0 3px rgba(65,105,225,0.08);
  }

  .dc-link {
    color: royalblue;
    cursor: pointer;
    font-size: 13px;
    transition: opacity 0.15s ease;
    display: inline;
  }

  .dc-link:hover {
    opacity: 0.8;
  }

  a[href='/store-login']:hover {
    color: #aaa !important;
  }

  @media (min-width: 768px) {
    .dc-btn {
      font-size: 17px !important;
      padding: 15px 24px !important;
    }
    .dc-input {
      font-size: 17px !important;
      padding: 14px 17px !important;
    }
    .dc-link {
      font-size: 15px !important;
    }
    .dc-toggle-btn {
      font-size: 16px !important;
    }
  }
`;

export default DineCentralLogin;